import { Injectable } from '@angular/core';
import { TextableService } from './textable-service.service';
import shortid from 'shortid';
import { LoadingController, AlertController } from '@ionic/angular';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { Router } from '@angular/router';
import { Camera, CameraResultType, CameraSource, ImageOptions } from '@capacitor/camera';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { getLogger } from '@shared/logging';
import { AuthNZService } from '../core/authnz.service';
import "src/polyfills/FirstEmitAsPromise.ts"
import { ContactDTO } from 'src/classes/ConversationModel/contactDTO';
import { TextableMessageStatusEvent } from '../backported_types/messaging';

const log = getLogger("MessagingService");

@Injectable({
  providedIn: 'root'
})
export class MessagingService {


  imageUrl: string = null;

  constructor(
    private textable: TextableService,
    private loading: LoadingController,
    private storage: AngularFireStorage,
    private router: Router,
    private alert: AlertController,
    private afs: AngularFirestore,
    private authnz: AuthNZService
  ) { }

  /**
    * Send Message takes a message string from the TextArea input and the target contact document to send outbound messages.
    @param {string} newMessage - Message String for Outbound Message Body.
    @param contact - Contact Document to send the Message to.
 */
  async sendMessage(newMessage:string, contact: ContactDTO) {
    if (newMessage !== '' || this.imageUrl !== '') {
      const activeNumber = await this.authnz.activeNumberProfile$.nThEmitAsPromise();

      let msg: TextableMessage = {
        contact_id: contact.id,
        direction: 'out',
        to: contact.PhoneNumber.Format(),
        from: activeNumber.phone_number,
        body: newMessage,
        send_status: 'pending',
        date: Date.now(),
        uid: activeNumber.firebase_document_id,
        attachments: [],
      };
    
      if (this.imageUrl != null) {
        this.setupBodyWithAttachment(msg);
      } else {
        this.storeMessage(msg);
      }
    }
  }

  async sendAsNote(note:string, contact) {
    if (note !== '') {
      let msg: TextableMessage = {
        contact_id: contact.id,
        direction: 'note',
        to: "",
        from: "",
        body: note,
        send_status: 'note',
        date: Date.now(),
        uid: (await this.authnz.activeNumberProfile$.nThEmitAsPromise()).firebase_document_id,
        attachments: [],
        type:'note'
      };
      this.storeMessage(msg);
    }
  }

  private async storeMessage(msg) {
    try {
      const s = await this.doTheSend(msg)
      this.router.navigateByUrl('/conversations/' + msg.contact_id, { replaceUrl: true });
      this.clearVariables();
    }
    catch (err)  {
      let alert  = await this.alert.create({
        header: 'Error saving message.',
        subHeader: 'There was a problem saving the message. Please try again.',
        buttons: ['OK']
      });
    }
  }
  
  
  async doTheSend(msg: TextableMessage) {

    if (!msg) {
      throw "Missing message.";
    }
    const currentUser = await(this.authnz.loggedInUserProfile$.nThEmitAsPromise());
    msg.sentBy =  currentUser.firebase_document_id;
    msg.sentByLabel = currentUser.full_name;
    if (!msg.body) {
      msg.body = '';
    }
    return Promise.all([
      this.afs.collection('messages').add(msg),
      this.afs.doc('contacts/' + msg.contact_id).update({ last_message: msg.body, last_message_date: Date.now(), isArchived: false })
    ]);

  }
  
  
 async attach() {
  const options: ImageOptions = {
    quality: 100,
    resultType: CameraResultType.Base64,
    source: CameraSource.Prompt
  };

   try {
    let photo = await Camera.getPhoto(options);
    this.imageUrl = 'data:image/jpeg;base64,' + photo.base64String;
    if (this.getDataURLSize(this.imageUrl) > 500000 ) {
      let loader = await this.loading.create({message: 'Resizing image...'});
      loader.present();
      let {size, data} = await this.resizeImageToTargetKB(this.imageUrl)
      this.imageUrl = data;
      loader.dismiss()
    }
   } catch (err) {
    alert("Error processing photo.");
  }
 }

 getDataURLSize(data: string): number {
  var binary = atob(data.split(',')[1]);
// Create 8-bit unsigned array
var array = [];
for(var i = 0; i < binary.length; i++) {
    array.push(binary.charCodeAt(i));
}
// Return our Blob object
 const b = new Blob([new Uint8Array(array)]);
 return b.size
}
async resizeImageToTargetKB(data: string, scale?: number): Promise<{size: number, data: string}> { 
  const targetSize = 500000
  const upSize = 1.5
  const downSize = 0.8

  if (!scale) {
    scale = (targetSize/this.getDataURLSize(data));
    if (scale<.5) {
      scale=2*scale
    }
  }
   log.info("Scaling " + scale)
  const newData = await this.scaleImage(data,scale);
  const newSize = this.getDataURLSize(newData);
  if (newSize < 0.9 * targetSize) {
     log.info("rescaled image was too small. tryting " + scale*upSize)
    return(await this.resizeImageToTargetKB(data,scale*upSize));
  }
  if (newSize < targetSize) {
     log.info("rescaled image was just right")
    return {size: newSize, data: newData}
  }
  else {
     log.info("rescaled image was too big. trying " + scale*downSize)
    return(await this.resizeImageToTargetKB(data,scale*downSize));
  }


  
}

async scaleImage(data: string, scale: number) {
  return new Promise<string>((resolve,reject) => {

        
    const img = document.createElement("img");
    img.src = data;
    img.onload = async () => {
      
      var width = img.width;
      var height = img.height;

      const resizeString = "Resizing from" + width+"x"+height 

      if (width > height) {
        const MAX_WIDTH = width * scale;
          if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
          }
      } else {
        const MAX_HEIGHT = height * scale;
          if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
          }
      }
      log.info(resizeString + " to " + width+"x"+height)
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      var ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      const scaledImage =  canvas.toDataURL("image/png");
      resolve(scaledImage);
   
     
    }
  });
}


  
  async setupBodyWithAttachment(msg) {
    let loader = await this.loading.create({message: 'Uploading...'});
    loader.present();

    const imgName = (await this.authnz.activeNumberProfile$.nThEmitAsPromise()).firebase_document_id + '/' + (await this.authnz.activeNumberProfile$.nThEmitAsPromise()).firebase_document_id + '_' + shortid() + '.jpg';
    try{ 
      const img  = this.storage.ref('mms/' + imgName);
      log.info('trying to save to mms/' + imgName);
      const savedImage  = await img.putString(this.imageUrl, 'data_url')
      const downloadURL = await img.getDownloadURL().toPromise();  // TODO: TXBDEV-3024 use firstValueFrom here once we've upgraded angular / RxJs

      log.info('saved image!');
      msg.attachments.push({
          url: downloadURL,
          contentType: 'image/jpeg', //TODO: We probably shouldn't hardcode this: https://textable.atlassian.net/browse/TXBDEV-809
          contentLength: this.getDataURLSize(this.imageUrl)
      });
      loader.dismiss();
      this.storeMessage(msg);
    }
    catch (error) {
      alert("Could not save photo.");
      loader.dismiss();
    }
  }

  clearVariables(){
    this.imageUrl = null;
  }
}

interface TextableMessage{
  id?:string;
  to?:string;
  from?:string;
  body?:string;
  attachments?:Array<any>;
  direction?:string;
  contact_id?:string;
  uid?:string;
  sentBy?:string;
  sentByLabel?:string;
  send_status?: "pending" | "scheduled" | "sent" | "fail" | "note";
  date?:number;
  type?:string
}
